// Admin service for fetching real system metrics
import { collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../config/firebase';
import { beebaseService } from './firebase';

export interface SystemMetrics {
  totalUsers: number;
  activeUsers: number;
  totalApiaries: number;
  totalHives: number;
  totalInspections: number;
  beebaseAlerts: number;
  bugReports: number;
}

export const adminService = {
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      console.log('🔍 AdminService: Fetching system metrics...');
      
      // Check authentication status
      const auth = getAuth();
      const currentUser = auth.currentUser;
      console.log('👤 Current user:', currentUser?.email);
      
      if (!currentUser) {
        console.error('❌ No authenticated user found');
        throw new Error('Authentication required');
      }
      
      try {
        const tokenResult = await currentUser.getIdTokenResult();
        console.log('🔐 Token claims:', tokenResult.claims);
        console.log('🛡️ Is admin:', tokenResult.claims.admin);
      } catch (tokenError) {
        console.error('❌ Error getting token:', tokenError);
      }
      
      // Get all collections counts
      const [
        usersSnapshot,
        apiariesSnapshot,
        hivesSnapshot,
        inspectionsSnapshot,
        reportsSnapshot
      ] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'apiaries')),
        getDocs(collection(db, 'hives')),
        getDocs(collection(db, 'inspections')),
        getDocs(collection(db, 'reports'))
      ]);

      console.log('📊 Collection counts:', {
        users: usersSnapshot.size,
        apiaries: apiariesSnapshot.size,
        hives: hivesSnapshot.size,
        inspections: inspectionsSnapshot.size,
        reports: reportsSnapshot.size
      });

      if (usersSnapshot.size === 0) {
        console.warn('⚠️ No users found in Firestore. This might indicate:');
        console.warn('   1. Users are only in Firebase Auth, not Firestore');
        console.warn('   2. Permission denied to read users collection');
        console.warn('   3. Users collection doesn\'t exist');
      }

      // Get active BeeBase alerts
      const activeAlerts = await beebaseService.getAllActiveAlerts();

      // Calculate active users (users with activity in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentInspections = inspectionsSnapshot.docs.filter(doc => {
        const data = doc.data();
        const inspectionDate = new Date(data.date);
        return inspectionDate >= thirtyDaysAgo;
      });

      // Get unique user IDs from recent inspections
      const activeUserIds = new Set(recentInspections.map(doc => doc.data().user_id));

      const metrics = {
        totalUsers: usersSnapshot.size,
        activeUsers: activeUserIds.size,
        totalApiaries: apiariesSnapshot.size,
        totalHives: hivesSnapshot.size,
        totalInspections: inspectionsSnapshot.size,
        beebaseAlerts: activeAlerts.length,
        bugReports: reportsSnapshot.size
      };

      console.log('✅ AdminService: Final metrics:', metrics);
      
      return metrics;
    } catch (error) {
      console.error('Error fetching admin metrics:', error);
      throw error;
    }
  },

  async getBugReports() {
    try {
      const reportsSnapshot = await getDocs(collection(db, 'reports'));
      
      return reportsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      })).sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error('Error fetching bug reports:', error);
      throw error;
    }
  }
};
